import React, { useEffect, useMemo, useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { Modal } from '../../../reusable';
import {
  MONTH_OPTIONS,
  getAvailableYearsFromDates,
  parseDateSafe,
} from '../../../placeholders/periodSummaryUtils';
import {
  exportVentasRegisterXlsx,
  getRegisterDocuments,
} from '../../../../services/treasuryAPI';

const money = (value) =>
  new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const formatSegment = (value) => {
  const raw = String(value || '').trim();
  return raw || 'Sin Asignar';
};

const formatDateIso = (date) => {
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/**
 * Modal para exportar libro de ventas a Excel.
 * El segmento de cada factura viene de la organización de la cotización
 * asociada a la venta vinculada al documento.
 */
const AccountingVentasExportModal = ({
  isOpen = false,
  onClose,
  rows = [],
  isDarkMode = false,
  ui,
}) => {
  const now = new Date();
  const availableYears = useMemo(() => {
    const fromRows = getAvailableYearsFromDates(
      rows.map((row) => parseDateSafe(row.doc_date)).filter(Boolean)
    );
    return fromRows.length ? fromRows : [now.getFullYear()];
  }, [rows]);

  const [year, setYear] = useState(String(now.getFullYear()));
  const [month, setMonth] = useState(String(now.getMonth()));
  const [includeItems, setIncludeItems] = useState(true);
  const [periodDocs, setPeriodDocs] = useState([]);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    const years = availableYears.length ? availableYears : [now.getFullYear()];
    const preferred = years.includes(now.getFullYear())
      ? now.getFullYear()
      : years[years.length - 1];
    setYear(String(preferred));
    setMonth(String(now.getMonth()));
    setIncludeItems(true);
    setError('');
  }, [isOpen, availableYears]);

  const periodRange = useMemo(() => {
    const y = Number(year);
    if (month === 'all') {
      return {
        dateFrom: formatDateIso(new Date(y, 0, 1)),
        dateTo: formatDateIso(new Date(y, 11, 31)),
        label: `Año ${y}`,
      };
    }
    const m = Number(month);
    const start = new Date(y, m, 1);
    const end = new Date(y, m + 1, 0);
    const monthLabel = MONTH_OPTIONS.find((item) => item.value === String(m))?.label || '';
    return {
      dateFrom: formatDateIso(start),
      dateTo: formatDateIso(end),
      label: `${monthLabel} ${y}`,
    };
  }, [year, month]);

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    (async () => {
      try {
        setIsLoadingPreview(true);
        setError('');
        const payload = await getRegisterDocuments({
          register_type: 'ventas',
          date_from: periodRange.dateFrom,
          date_to: periodRange.dateTo,
        });
        if (cancelled) return;
        const list = Array.isArray(payload) ? payload : [];
        list.sort((a, b) => String(a.doc_date || '').localeCompare(String(b.doc_date || '')));
        setPeriodDocs(list);
      } catch (err) {
        if (cancelled) return;
        setPeriodDocs([]);
        setError(err.message || 'No se pudo cargar el período.');
      } finally {
        if (!cancelled) setIsLoadingPreview(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, periodRange.dateFrom, periodRange.dateTo]);

  const segmentSummary = useMemo(() => {
    const counts = {};
    periodDocs.forEach((row) => {
      const key = formatSegment(row.segment);
      counts[key] = (counts[key] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => a[0].localeCompare(b[0], 'es'));
  }, [periodDocs]);

  const inputStyle = {
    height: 34,
    borderRadius: 8,
    border: `1px solid ${ui?.inputBorder || '#d4d4d8'}`,
    background: ui?.inputBg || '#fff',
    color: ui?.inputText || ui?.text || '#111',
    padding: '0 10px',
    fontSize: 13,
    boxSizing: 'border-box',
  };

  const handleExport = async () => {
    if (!periodDocs.length) {
      setError('No hay documentos en el período seleccionado.');
      return;
    }
    try {
      setIsExporting(true);
      setError('');
      await exportVentasRegisterXlsx({
        date_from: periodRange.dateFrom,
        date_to: periodRange.dateTo,
        include_items: includeItems,
      });
      onClose?.();
    } catch (err) {
      setError(err.message || 'No se pudo exportar el Excel.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Exportar libro de ventas"
      subtitle="El segmento de cada factura sale de la organización de la cotización vinculada a su venta."
      size="lg"
      isDarkMode={isDarkMode}
      closeOnOverlayClick={!isExporting}
      closeOnEscape={!isExporting}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, width: '100%' }}>
          <button
            type="button"
            onClick={onClose}
            disabled={isExporting}
            style={{
              height: 36,
              padding: '0 14px',
              borderRadius: 8,
              border: `1px solid ${ui?.inputBorder || '#d4d4d8'}`,
              background: ui?.inputBg || '#fff',
              color: ui?.text || '#111',
              fontWeight: 600,
              fontSize: 13,
              cursor: isExporting ? 'wait' : 'pointer',
            }}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleExport}
            disabled={isExporting || isLoadingPreview || !periodDocs.length}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              height: 36,
              padding: '0 14px',
              borderRadius: 8,
              border: 'none',
              background: '#16a34a',
              color: '#fff',
              fontWeight: 600,
              fontSize: 13,
              cursor:
                isExporting || isLoadingPreview || !periodDocs.length ? 'not-allowed' : 'pointer',
              opacity: isExporting || isLoadingPreview || !periodDocs.length ? 0.7 : 1,
            }}
          >
            {isExporting ? <Loader2 size={16} /> : <Download size={16} />}
            {isExporting ? 'Exportando…' : 'Descargar Excel'}
          </button>
        </div>
      }
    >
      <div style={{ display: 'grid', gap: 14 }}>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 12,
            alignItems: 'flex-end',
          }}
        >
          <label style={{ display: 'grid', gap: 4 }}>
            <span style={{ fontSize: 12, color: ui?.muted }}>Año</span>
            <select value={year} onChange={(e) => setYear(e.target.value)} style={{ ...inputStyle, minWidth: 100 }}>
              {availableYears.map((value) => (
                <option key={value} value={String(value)}>
                  {value}
                </option>
              ))}
            </select>
          </label>
          <label style={{ display: 'grid', gap: 4 }}>
            <span style={{ fontSize: 12, color: ui?.muted }}>Mes</span>
            <select value={month} onChange={(e) => setMonth(e.target.value)} style={{ ...inputStyle, minWidth: 140 }}>
              <option value="all">Todo el año</option>
              {MONTH_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              height: 34,
              fontSize: 13,
              color: ui?.text,
              cursor: 'pointer',
              marginBottom: 1,
            }}
          >
            <input
              type="checkbox"
              checked={includeItems}
              onChange={(e) => setIncludeItems(e.target.checked)}
            />
            Incluir detalle de ítems (neto, IVA y C/IVA)
          </label>
        </div>

        <div
          style={{
            padding: '10px 12px',
            borderRadius: 8,
            border: `1px solid ${ui?.border || '#e4e4e7'}`,
            background: ui?.panel || 'transparent',
            fontSize: 13,
            color: ui?.muted,
          }}
        >
          Período: <strong style={{ color: ui?.text }}>{periodRange.label}</strong>
          {' · '}
          {isLoadingPreview ? 'Cargando…' : `${periodDocs.length} documento(s)`}
          {!isLoadingPreview && includeItems ? ' · con ítems vinculados' : ''}
          {!isLoadingPreview && segmentSummary.length ? (
            <>
              <br />
              Segmentos:{' '}
              {segmentSummary.map(([name, count], index) => (
                <span key={name}>
                  {index > 0 ? ' · ' : ''}
                  <strong style={{ color: ui?.text }}>{name}</strong> ({count})
                </span>
              ))}
            </>
          ) : null}
        </div>

        {error ? (
          <p style={{ margin: 0, fontSize: 13, color: ui?.danger || '#dc2626' }}>{error}</p>
        ) : null}

        <div
          style={{
            border: `1px solid ${ui?.border || '#e4e4e7'}`,
            borderRadius: 8,
            overflow: 'auto',
            maxHeight: 360,
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: ui?.header || '#f4f4f5', textAlign: 'left' }}>
                <th style={{ padding: '8px 10px', position: 'sticky', top: 0 }}>Fecha</th>
                <th style={{ padding: '8px 10px', position: 'sticky', top: 0 }}>Folio</th>
                <th style={{ padding: '8px 10px', position: 'sticky', top: 0 }}>Cliente</th>
                <th style={{ padding: '8px 10px', position: 'sticky', top: 0 }}>Venta</th>
                <th style={{ padding: '8px 10px', position: 'sticky', top: 0 }}>Total</th>
                <th style={{ padding: '8px 10px', position: 'sticky', top: 0 }}>Segmento</th>
              </tr>
            </thead>
            <tbody>
              {isLoadingPreview ? (
                <tr>
                  <td colSpan={6} style={{ padding: 16, color: ui?.muted, textAlign: 'center' }}>
                    Cargando documentos del período…
                  </td>
                </tr>
              ) : periodDocs.length ? (
                periodDocs.map((row) => (
                  <tr key={row.id} style={{ borderTop: `1px solid ${ui?.border || '#e4e4e7'}` }}>
                    <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                      {String(row.doc_date || '').slice(0, 10)}
                    </td>
                    <td style={{ padding: '8px 10px' }}>{row.doc_number || '—'}</td>
                    <td style={{ padding: '8px 10px' }}>
                      <div style={{ fontWeight: 600 }}>{row.counterpart_name || '—'}</div>
                      <div style={{ color: ui?.muted }}>{row.counterpart_rut || ''}</div>
                    </td>
                    <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                      {row.sale_code || '—'}
                    </td>
                    <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                      {money(row.total_amount)}
                    </td>
                    <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                      {formatSegment(row.segment)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} style={{ padding: 16, color: ui?.muted, textAlign: 'center' }}>
                    No hay documentos en este período.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Modal>
  );
};

export default AccountingVentasExportModal;
